import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setProject, setProjectLink, setReleaseId, setSimpleUserList, clearProject, clearRelease } from '@Redux/project/projectActions';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import UrlEncryptionService from '@Helpers/UrlEncryptionService';

export const useReduxProject = () => {

    const dispatch = useDispatch();
    const urlEncryptionService = new UrlEncryptionService();
    const projectService = new ProjectService();

    const project = useSelector((state) => state.project.currentProject);
    const projectLinkList = useSelector((state) => state.project.projectLink);
    const currentReleaseId = useSelector((state) => state.project.releaseId);
    const simpleUsers = useSelector((state) => state.project.simpleUsers);
    const projectId = project?.id ?? 0;
    const projectTitle = project?.projectName ?? '';
    const encryptedProjectId = project?.encryptedProjectId ?? '';
    const permission = useSelector((state) => state.activeUser.permission);
    const accessList = useSelector((state) => state.activeUser.accessList);
    const currentUserProjectPermissions = !!permission.projects ? permission?.projects[projectId] : {};

    const setProjectValue = useCallback(
       async (projectData) => {
            let updatedProject = {
                ...projectData,
                encryptedProjectId : await encryptProjectId(projectData.id)
            }
            dispatch(clearRelease());
            dispatch(setProject(updatedProject))
        },
        [dispatch]
    );

    const setProjectValueWithId = useCallback(
        async (projectData, encryptedId) => {
            let updatedProject = {
                ...projectData,
                encryptedProjectId: encryptedId
            };
            dispatch(clearRelease());
            dispatch(setProject(updatedProject));
        },
        [dispatch]
    );

    const setCurrentProjectById = useCallback(
        (id) => {
            let projects = accessList.projects;
            let findProject = projects.find((project) => project.id === id);
            if(!!findProject) {
                setProjectValue(findProject);
            }
        },
        [dispatch]
    );

    const setUserList = useCallback(
        (id, usersList) => {
            const uniqueUsers = [...new Set(usersList)];
            let bodyObj = {
                ...simpleUsers,
                [id]: uniqueUsers,
            }
            return dispatch(setSimpleUserList(bodyObj));
        },
        [dispatch]
    );

    const removeSimpleUserByUserId = useCallback(
        (userId, projectId) => {
            let usersList = simpleUsers;
            if(!!usersList[projectId]) {
                let usersInProject = usersList[projectId];
                usersList[projectId] = usersInProject.filter((user)=> user.id !== userId);
            }
            return dispatch(setSimpleUserList(usersList));
        },
        [dispatch]
    );

    const getSimpleUsersByProjectId = async (id)=> {
        let usersList = [];
        if(!!simpleUsers) {
            if(!!simpleUsers[id]) {
                return simpleUsers[id];
            } else {
                usersList = await fetchUsersList(id);
                setUserList(id, usersList);
                return usersList;
            }
        } else {
            usersList = await fetchUsersList(id);
            setUserList(id, usersList);
            return usersList;
        }
    }

    const fetchUsersList = async(id)=> {
        let userListObj = await projectService.getSimpleAssigneeUserListForProject(id);
        userListObj = JSON.parse(userListObj.body);
        return userListObj;
    }

    const forceUpdateUserListInProject = async(projectId)=> {
        let usersList = await fetchUsersList(projectId);
        setUserList(projectId, usersList);
        return usersList;
    }

    const getUserPermissionByProjectId = (projectId)=> {
        return !!permission.projects ? permission?.projects[projectId] : {};
    }

    const setCurrentReleaseId = useCallback(
        (releaseId) => dispatch(setReleaseId(releaseId)),
        [dispatch]
    );

    const clearReleaseValue = useCallback(
        () => dispatch(clearRelease()),
        [dispatch]
    );

    const setProjectLinkList = useCallback(
        (linkList) => dispatch(setProjectLink(linkList)),
        [dispatch]
    );

    const clearProjectValue = useCallback(
        () => dispatch(clearProject()),
        [dispatch]
    );

    const encryptProjectId = async (projectId)=> {
        return await urlEncryptionService.encryptData(projectId);
    }

    return {
        currentProject: project,
        currentProjectId: projectId,
        encryptedProjectId: encryptedProjectId,
        currentProjectTitle: projectTitle,
        setCurrentProject: setProjectValue,
        projectLinkList: projectLinkList,
        setProjectLinkList : setProjectLinkList,
        currentReleaseId: currentReleaseId,
        setCurrentReleaseId: setCurrentReleaseId,
        setCurrentProjectWithId: setProjectValueWithId,
        clearCurrentProject: clearProjectValue,
        currentUserProjectPermissions: currentUserProjectPermissions,
        setCurrentProjectById: setCurrentProjectById,
        getSimpleUsersByProjectId: getSimpleUsersByProjectId,
        getUserPermissionByProjectId: getUserPermissionByProjectId,
        removeSimpleUserByUserId: removeSimpleUserByUserId,
        forceUpdateUserListInProject: forceUpdateUserListInProject
    }
}